// SPDX-FileCopyrightText: 2026 Angels' Ware
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  Inbox,
  Workflow,
  Plus,
  ArrowRight,
  Share2,
} from "lucide-react";
import { useAuth } from "../auth";
import { api } from "../api";
import { formatRelative, formatDateTime, formatDate } from "../lib/datetime";
import { ShareOverviewModal } from "../components/dialogs/ShareOverviewModal";
import { Button } from "../components/ui/Button";
import type { FlowSummary, RunSummary } from "../types";
import { ICON } from "../icons";
import { StatCard } from "../components/ui/StatCard";

const RUN_WINDOW = 200; // recent runs to summarize
const ATTENTION_MAX = 5;
const RECENT_MAX = 8;

function startOfToday(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function Dashboard() {
  const { t } = useTranslation();
  const { token, me, activeTenant, activeWorkspace } = useAuth();
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [approvalCount, setApprovalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!token || !activeWorkspace) return;
    let cancelled = false;
    setLoading(true);
    const tenant = activeTenant || undefined;
    const workspace = activeWorkspace || undefined;
    Promise.allSettled([
      api.listAllRuns(token, { limit: RUN_WINDOW, workspace, tenant }),
      api.listGraphs(token, activeTenant, activeWorkspace),
      api.countPendingApprovals(token, { workspace, tenant }),
    ]).then(([r, g, a]) => {
      if (cancelled) return;
      if (r.status === "fulfilled") setRuns(r.value.runs ?? []);
      if (g.status === "fulfilled") setFlows(g.value.graphs ?? []);
      if (a.status === "fulfilled") setApprovalCount(a.value.count ?? 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token, activeTenant, activeWorkspace]);

  // Only "real" flows count toward the overview. Private flows are scoped to a
  // single owner (the public board hides them too); UNPUBLISHED flows are
  // drafts whatever their trigger (their runs are test runs); and paused
  // (disabled) flows are intentionally off. None should skew the health stats
  // or show as "needs attention" — a draft's or a disabled flow's last failure
  // isn't actionable. We exclude both the flows and any run belonging to them.
  // `published` may be undefined on older daemons — treat only an explicit
  // false as unpublished so the stats don't empty out against a stale backend.
  const countedFlows = useMemo(
    () =>
      flows.filter(
        (f) =>
          f.visibility !== "private" &&
          f.published !== false &&
          f.run_status !== "paused",
      ),
    [flows],
  );
  const countedRuns = useMemo(() => {
    const ids = new Set(countedFlows.map((f) => f.id));
    return runs.filter((r) => ids.has(r.graph_id));
  }, [runs, countedFlows]);

  const stats = useMemo(() => {
    const today = startOfToday();
    const runsToday = countedRuns.filter((r) => {
      const ts = Date.parse(r.started_at || r.enqueued_at || "");
      return Number.isFinite(ts) && ts >= today;
    }).length;
    const finished = countedRuns.filter(
      (r) => r.status === "succeeded" || r.status === "failed",
    );
    const succeeded = finished.filter((r) => r.status === "succeeded").length;
    const successRate = finished.length
      ? Math.round((succeeded / finished.length) * 100)
      : null;
    const liveFlows = countedFlows.filter((f) => f.run_status === "live").length;
    return { runsToday, successRate, finishedCount: finished.length, liveFlows };
  }, [countedRuns, countedFlows]);

  // A flow needs attention when its MOST RECENT run failed — not every
  // historical failure. Runs arrive newest-first (ORDER BY enqueued_at DESC),
  // so the first run we see for a graph is its latest; once that flow runs
  // again — succeeds, or is back to running/pending — it drops off this list.
  // One entry per flow so a flaky flow doesn't flood the count. Uncapped: the
  // stat card shows the true total (and must match the public board, which
  // counts the same way); the panel below renders only the first ATTENTION_MAX.
  const attentionRuns = useMemo(() => {
    const seen = new Set<string>();
    const out: RunSummary[] = [];
    for (const r of countedRuns) {
      if (seen.has(r.graph_id)) continue; // already saw this flow's latest run
      seen.add(r.graph_id);
      if (r.status === "failed") out.push(r);
    }
    return out;
  }, [countedRuns]);
  const recentRuns = useMemo(
    () => countedRuns.slice(0, RECENT_MAX),
    [countedRuns],
  );
  const flowName = (id: string) =>
    flows.find((f) => f.id === id)?.name || id;

  const today = formatDate(new Date());

  const greeting = me?.subject ? me.subject.split("@")[0] : "";

  return (
    <div>
      <div className="page-title">
        <div>
          <h1>{t("dashboard.title")}</h1>
          <div className="sub">
            {greeting
              ? t("dashboard.greeting", { name: greeting })
              : t("dashboard.subtitle")}
          </div>
        </div>
        <div className="dash-title-actions">
          <Button
            className="dash-share"
            onClick={() => setShareOpen(true)}
            title={t("share.title")}
          >
            <Share2 size={ICON.md} />
            {t("share.action")}
          </Button>
          <Link to="/flows/new" className="btn primary dash-new">
            <Plus className="icon-lede" size={ICON.md} />
            {t("flowList.newFlow")}
          </Link>
        </div>
      </div>

      {shareOpen && <ShareOverviewModal onClose={() => setShareOpen(false)} />}

      <div className="dash-stats">
        <StatCard
          icon={<Activity size={ICON.lg} />}
          label={t("dashboard.runsToday")}
          value={loading ? "—" : String(stats.runsToday)}
          to={`/runs?since=${today}&until=${today}`}
        />
        <StatCard
          icon={<CheckCircle2 size={ICON.lg} />}
          label={t("dashboard.successRate")}
          value={
            loading || stats.successRate === null
              ? "—"
              : `${stats.successRate}%`
          }
          sub={
            stats.finishedCount
              ? t("dashboard.overRuns", { count: stats.finishedCount })
              : undefined
          }
          tone={
            stats.successRate !== null && stats.successRate < 80
              ? "warn"
              : "good"
          }
          to="/runs?status=succeeded"
        />
        <StatCard
          icon={<AlertTriangle size={ICON.lg} />}
          label={t("dashboard.needsAttention")}
          value={loading ? "—" : String(attentionRuns.length)}
          tone={attentionRuns.length > 0 ? "bad" : "good"}
          to="/runs?status=failed"
        />
        <StatCard
          icon={<Inbox size={ICON.lg} />}
          label={t("dashboard.approvalsWaiting")}
          value={loading ? "—" : String(approvalCount)}
          tone={approvalCount > 0 ? "warn" : "good"}
          to="/approvals"
        />
      </div>

      <div className="dash-grid">
        {/* Failures to act on — the operator's first stop after "is anything
            broken?". Empty state reads as reassurance, not a void. */}
        <section className="card dash-panel">
          <div className="dash-panel-head">
            <h2>{t("dashboard.needsAttention")}</h2>
            {attentionRuns.length > 0 && (
              <Link to="/runs?status=failed" className="dash-panel-link">
                {t("dashboard.viewAll")} <ArrowRight size={ICON.sm} />
              </Link>
            )}
          </div>
          {loading ? (
            <p className="dash-empty">{t("common.loading")}</p>
          ) : attentionRuns.length === 0 ? (
            <p className="dash-empty dash-empty-good">
              <CheckCircle2 size={ICON.md} />
              {t("dashboard.noFailures")}
            </p>
          ) : (
            <ul className="dash-runlist">
              {attentionRuns.slice(0, ATTENTION_MAX).map((r) => (
                <li key={r.id}>
                  <Link to={`/runs/${encodeURIComponent(r.id)}`}>
                    <span className="status-dot failed" />
                    <span className="dash-run-flow">{flowName(r.graph_id)}</span>
                    {/* The raw error_code is a technical token; on the
                        overview the failed dot + flow name say enough, and the
                        run detail explains the cause in plain language. */}
                    <span
                      className="dash-run-time"
                      title={formatDateTime(r.started_at || r.enqueued_at)}
                    >
                      {formatRelative(r.started_at || r.enqueued_at, t)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Recent activity — the live pulse of the workspace. */}
        <section className="card dash-panel">
          <div className="dash-panel-head">
            <h2>{t("dashboard.recentActivity")}</h2>
            <Link to="/runs" className="dash-panel-link">
              {t("dashboard.viewAll")} <ArrowRight size={ICON.sm} />
            </Link>
          </div>
          {loading ? (
            <p className="dash-empty">{t("common.loading")}</p>
          ) : recentRuns.length === 0 ? (
            <p className="dash-empty">{t("dashboard.noRuns")}</p>
          ) : (
            <ul className="dash-runlist">
              {recentRuns.map((r) => (
                <li key={r.id}>
                  <Link to={`/runs/${encodeURIComponent(r.id)}`}>
                    <span className={"status-dot " + r.status} />
                    <span className="dash-run-flow">{flowName(r.graph_id)}</span>
                    <span
                      className="dash-run-time"
                      title={formatDateTime(r.started_at || r.enqueued_at)}
                    >
                      {formatRelative(r.started_at || r.enqueued_at, t)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Footer line: flow inventory, with a quick jump to the full list. */}
      <div className="dash-foot">
        <Workflow size={ICON.sm} />
        {t("dashboard.flowSummary", {
          total: countedFlows.length,
          live: stats.liveFlows,
        })}
        <Link to="/flows" className="dash-panel-link">
          {t("dashboard.manageFlows")} <ArrowRight size={ICON.sm} />
        </Link>
      </div>
    </div>
  );
}

